import React, { useEffect, useState, useCallback } from 'react';
import { toast } from 'sonner';
import { Archive, ArchiveRestore, RotateCcw, Trash2, Eye, RefreshCw } from 'lucide-react';

/**
 * Unified Library view: works for both Video Studio (studio='video' -> scripts)
 * and Post Studio (studio='post' -> posts + carousels + threads).
 *
 * Three status views: active | archived | trashed
 * Filter pills: All | Generator | Remix | Repurpose | Refined | From Script | Carousel | Thread
 * Per-row actions:
 *   active   -> Open, Archive, Trash
 *   archived -> Restore, Trash
 *   trashed  -> Restore, Delete Forever (+ shows auto-purge countdown)
 */

const SOURCE_PILLS_VIDEO = [
  { key: 'all',        label: 'All' },
  { key: 'generator',  label: 'Generated' },
  { key: 'remix',      label: 'Remixed' },
  { key: 'repurpose',  label: 'Repurposed' },
  { key: 'hook_lab',   label: 'Hook Lab' },
  { key: 'video_from_post', label: 'From Post' },
];

const SOURCE_PILLS_POST = [
  { key: 'all',        label: 'All' },
  { key: 'generator',  label: 'Generated' },
  { key: 'remix',      label: 'Remixed' },
  { key: 'hook_lab',   label: 'Hook Lab' },
  { key: 'from_script', label: 'From Script' },
  { key: 'carousel',   label: 'Carousels' },
  { key: 'thread',     label: 'Threads' },
];

const STATUS_TABS = [
  { key: 'active',   label: 'Active',   icon: Eye },
  { key: 'archived', label: 'Archived', icon: Archive },
  { key: 'trashed',  label: 'Trash',    icon: Trash2 },
];

export function UnifiedLibrary({ studio, api, profiles, onOpenItem }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statusView, setStatusView] = useState('active');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [platformFilter, setPlatformFilter] = useState('');
  const [profileFilter, setProfileFilter] = useState('');
  const [q, setQ] = useState('');

  const sourcePills = studio === 'video' ? SOURCE_PILLS_VIDEO : SOURCE_PILLS_POST;

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ studio, status: statusView, limit: '200' });
      if (sourceFilter && sourceFilter !== 'all') params.set('source', sourceFilter);
      if (platformFilter) params.set('platform', platformFilter);
      if (profileFilter) params.set('profile_id', profileFilter);
      if (q.trim()) params.set('q', q.trim());
      const r = await api.get(`/api/admin/social-studio/library?${params.toString()}`);
      setItems(r.data.items || []);
    } catch (e) {
      toast.error('Library load failed: ' + (e.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
  }, [studio, statusView, sourceFilter, platformFilter, profileFilter, q, api]);

  useEffect(() => { refresh(); }, [refresh]);

  const handleAction = async (item, action) => {
    const verb = { archive: 'archived', unarchive: 'unarchived', trash: 'moved to trash', restore: 'restored', permanent: 'permanently deleted' }[action] || action;
    if (action === 'permanent') {
      // eslint-disable-next-line no-restricted-globals
      if (!confirm(`Permanently delete "${(item.title || item.id).slice(0, 60)}"? This cannot be undone.`)) return;
    }
    const isDelete = action === 'permanent';
    const method = isDelete ? 'delete' : 'post';
    const url = `/api/admin/social-studio/library/${item.kind}/${item.id}/${action === 'unarchive' ? 'unarchive' : action === 'permanent' ? 'permanent' : action}`;
    try {
      await api[method](url);
      toast.success(`${item.kind} ${verb}`);
      refresh();
    } catch (e) {
      toast.error(`Action failed: ${e.response?.data?.detail || e.message}`);
    }
  };

  // Compute days-until-purge for trashed items (30-day retention)
  const daysUntilPurge = (deletedAt) => {
    if (!deletedAt) return null;
    const d = new Date(deletedAt);
    const days = 30 - Math.floor((Date.now() - d.getTime()) / (1000 * 60 * 60 * 24));
    return Math.max(0, days);
  };

  const sourceColor = (s) => ({
    generator: 'bg-blue-100 text-blue-800',
    remix: 'bg-fuchsia-100 text-fuchsia-800',
    repurpose: 'bg-amber-100 text-amber-800',
    refined: 'bg-emerald-100 text-emerald-800',
    from_script: 'bg-indigo-100 text-indigo-800',
    video_from_post: 'bg-indigo-100 text-indigo-800',
    carousel: 'bg-purple-100 text-purple-800',
    thread: 'bg-sky-100 text-sky-800',
    hook_lab: 'bg-pink-100 text-pink-800',
  }[s] || 'bg-slate-100 text-slate-700');

  return (
    <div className="bg-white border border-[#E2E8F0] rounded-2xl p-4 space-y-4" data-testid={`unified-library-${studio}`}>
      {/* Status tabs */}
      <div className="flex items-center gap-2 border-b border-[#E2E8F0] -mt-1 -mx-1 px-1">
        {STATUS_TABS.map((s) => {
          const Icon = s.icon;
          const active = statusView === s.key;
          return (
            <button
              key={s.key}
              type="button"
              onClick={() => setStatusView(s.key)}
              data-testid={`library-status-${s.key}`}
              className={`inline-flex items-center gap-1.5 px-3 py-2 text-xs font-semibold border-b-2 -mb-px transition ${active ? 'text-[#1D4ED8] border-[#1D4ED8]' : 'text-[#64748B] border-transparent hover:text-[#0A0F1C]'}`}
            >
              <Icon size={13} aria-hidden="true" />
              {s.label}
            </button>
          );
        })}
        <button type="button" onClick={refresh} className="ml-auto text-[11px] text-[#1D4ED8] inline-flex items-center gap-1 hover:underline" data-testid="library-refresh">
          <RefreshCw size={11} className={loading ? 'animate-spin' : ''} aria-hidden="true" /> Refresh
        </button>
      </div>

      {/* Source filter pills */}
      <div className="flex flex-wrap gap-1.5" data-testid="library-source-pills">
        {sourcePills.map((p) => (
          <button
            key={p.key}
            type="button"
            onClick={() => setSourceFilter(p.key)}
            data-testid={`library-source-${p.key}`}
            className={`px-3 py-1 text-[11px] font-semibold rounded-full transition ${sourceFilter === p.key ? 'bg-[#0A0F1C] text-white' : 'bg-[#F1F5F9] text-[#475569] hover:bg-[#E2E8F0]'}`}
          >
            {p.label}
          </button>
        ))}
      </div>

      {/* Search + brand filter */}
      <div className="grid sm:grid-cols-3 gap-2">
        <input
          type="text"
          placeholder="Search topic..."
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') refresh(); }}
          data-testid="library-search"
          className="px-2 py-1.5 border border-[#E2E8F0] rounded-lg text-xs"
        />
        <select
          value={profileFilter}
          onChange={(e) => setProfileFilter(e.target.value)}
          data-testid="library-brand-filter"
          className="px-2 py-1.5 border border-[#E2E8F0] rounded-lg text-xs"
        >
          <option value="">All brands</option>
          {(profiles || []).map((p) => <option key={p.profile_id} value={p.profile_id}>{p.name}</option>)}
        </select>
        <input
          type="text"
          placeholder="Platform (e.g. tiktok, facebook)"
          value={platformFilter}
          onChange={(e) => setPlatformFilter(e.target.value)}
          onBlur={refresh}
          onKeyDown={(e) => { if (e.key === 'Enter') refresh(); }}
          data-testid="library-platform-filter"
          className="px-2 py-1.5 border border-[#E2E8F0] rounded-lg text-xs"
        />
      </div>

      {/* Item count */}
      <div className="text-[11px] text-[#64748B]" data-testid="library-count">
        {loading ? 'Loading...' : `${items.length} item${items.length === 1 ? '' : 's'} in ${statusView}`}
      </div>

      {/* List */}
      {items.length === 0 ? (
        <div className="text-sm text-[#64748B] py-12 text-center" data-testid="library-empty">
          {statusView === 'trashed' ? 'Trash is empty.' : statusView === 'archived' ? 'No archived items.' : 'Nothing here yet. Generate some content.'}
        </div>
      ) : (
        <ul className="divide-y divide-[#F1F5F9]" data-testid="library-list">
          {items.map((item) => {
            const days = item.library_status === 'trashed' ? daysUntilPurge(item.deleted_at) : null;
            return (
              <li key={`${item.kind}_${item.id}`} className="py-3 flex items-center gap-3 flex-wrap" data-testid={`library-row-${item.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${sourceColor(item.source)}`} data-testid={`source-pill-${item.id}`}>
                      {item.source}
                    </span>
                    <span className="text-[10px] uppercase tracking-wider text-[#64748B]">{item.kind}</span>
                    {item.platform && <span className="text-[10px] uppercase tracking-wider text-[#94A3B8]">{item.platform}</span>}
                    {days !== null && (
                      <span className="text-[10px] font-semibold uppercase tracking-wider text-rose-600" data-testid={`purge-countdown-${item.id}`}>
                        auto-deletes in {days} day{days === 1 ? '' : 's'}
                      </span>
                    )}
                  </div>
                  <div className="font-semibold text-sm text-[#0A0F1C] truncate">{item.title || '(untitled)'}</div>
                  <div className="text-xs text-[#64748B] truncate">{item.snippet || ''}</div>
                </div>

                <div className="flex items-center gap-1.5 flex-wrap">
                  {statusView === 'active' && (
                    <>
                      <button type="button" onClick={() => onOpenItem && onOpenItem(item)} data-testid={`library-open-${item.id}`}
                        className="px-2 py-1 text-[10px] font-semibold bg-[#0A0F1C] text-white rounded hover:bg-[#1E293B] inline-flex items-center gap-1">
                        <Eye size={11} aria-hidden="true" /> Open
                      </button>
                      <button type="button" onClick={() => handleAction(item, 'archive')} data-testid={`library-archive-${item.id}`}
                        className="px-2 py-1 text-[10px] font-semibold bg-white border border-[#CBD5E1] rounded hover:bg-[#F1F5F9] inline-flex items-center gap-1">
                        <Archive size={11} aria-hidden="true" /> Archive
                      </button>
                      <button type="button" onClick={() => handleAction(item, 'trash')} data-testid={`library-trash-${item.id}`}
                        className="px-2 py-1 text-[10px] font-semibold bg-white border border-[#CBD5E1] text-rose-600 rounded hover:bg-rose-50 inline-flex items-center gap-1">
                        <Trash2 size={11} aria-hidden="true" /> Trash
                      </button>
                    </>
                  )}
                  {statusView === 'archived' && (
                    <>
                      <button type="button" onClick={() => handleAction(item, 'unarchive')} data-testid={`library-restore-${item.id}`}
                        className="px-2 py-1 text-[10px] font-semibold bg-[#0A0F1C] text-white rounded hover:bg-[#1E293B] inline-flex items-center gap-1">
                        <ArchiveRestore size={11} aria-hidden="true" /> Restore
                      </button>
                      <button type="button" onClick={() => handleAction(item, 'trash')} data-testid={`library-trash-${item.id}`}
                        className="px-2 py-1 text-[10px] font-semibold bg-white border border-[#CBD5E1] text-rose-600 rounded hover:bg-rose-50 inline-flex items-center gap-1">
                        <Trash2 size={11} aria-hidden="true" /> Trash
                      </button>
                    </>
                  )}
                  {statusView === 'trashed' && (
                    <>
                      <button type="button" onClick={() => handleAction(item, 'restore')} data-testid={`library-restore-${item.id}`}
                        className="px-2 py-1 text-[10px] font-semibold bg-[#0A0F1C] text-white rounded hover:bg-[#1E293B] inline-flex items-center gap-1">
                        <RotateCcw size={11} aria-hidden="true" /> Restore
                      </button>
                      <button type="button" onClick={() => handleAction(item, 'permanent')} data-testid={`library-permanent-${item.id}`}
                        className="px-2 py-1 text-[10px] font-semibold bg-rose-600 text-white rounded hover:bg-rose-700 inline-flex items-center gap-1">
                        <Trash2 size={11} aria-hidden="true" /> Delete Forever
                      </button>
                    </>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
